import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isHomeResponse, OvidiusCreateCopyOfNoneditableFileAPICall } from 'reactApp/api/OvidiusCreateCopyOfNoneditableFileAPICall';
import { openEditor } from 'reactApp/appHelpers/appHelpers';
import {
    NotifyFrom,
    setNotificationAboutCreatingCopyOfNoneditableFile,
} from 'reactApp/appHelpers/notificationAboutCreatingCopyOfNoneditableFile';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { getFeatureEditingNoneditableFormats } from 'reactApp/modules/features/features.selectors';
import { loadHomeFolderRequest } from 'reactApp/modules/home/home.actions';
import { resetSelect, scrollToHomeItemAction } from 'reactApp/modules/selections/selections.actions';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { RootState } from 'reactApp/store';

import { sendTechAnalytics, TechAnalyticsType } from './sendTechAnalytics';
import { CreateCopyOfNonEditableFileModalProps } from './types';

const snackBarCommonProps = {
    closable: true,
    closeTimeout: 5000,
};

type Init = CreateCopyOfNonEditableFileModalProps & {
    isSharedFile: boolean;
    techAnalytics: ReturnType<typeof sendTechAnalytics>;
};

export const useCreateCopyOfFile = ({ file, convertedStorage, fromSharePopup, isSharedFile, onClose, techAnalytics }: Init) => {
    const dispatch = useDispatch();
    // В данном случае фича точно будет присутствовать, без её наличия модальное окно не откроется
    const { ext } = useSelector((state: RootState) =>
        getFeatureEditingNoneditableFormats(state, { file, storage: convertedStorage })
    ) as NonNullable<ReturnType<typeof getFeatureEditingNoneditableFormats>>;

    const handleCreateCopyOfFile = async () => {
        techAnalytics(TechAnalyticsType.clickPopupButton);
        techAnalytics(TechAnalyticsType.apiStart);

        try {
            const { data } = await new OvidiusCreateCopyOfNoneditableFileAPICall().makeRequest({ [convertedStorage]: file.id });

            if (isHomeResponse(data)) {
                dispatch(loadHomeFolderRequest({ id: file.parent, force: true }));

                techAnalytics(TechAnalyticsType.apiSuccess);

                if (fromSharePopup) {
                    dispatch(resetSelect());
                    dispatch(scrollToHomeItemAction({ parentFolderId: file.parent, itemId: data.home }));
                    onClose?.();

                    snackbarController.showSnackbar({
                        id: 'create-copy-of-non-editable-file-success',
                        text: 'Документ создан',
                        buttonText: 'Настроить доступ',
                        onButtonClick: () => {
                            toolbarActions.publish('editor', data.home);
                        },
                        type: SnackbarTypes.success,
                        oneline: true,
                        ...snackBarCommonProps,
                    });
                    return;
                }
                // Прокинуть уведомление о создании файла в редактор
                dispatch(
                    setNotificationAboutCreatingCopyOfNoneditableFile({
                        newId: data.home,
                        oldId: file.id,
                        from: isSharedFile ? NotifyFrom.sharedHome : NotifyFrom.home,
                    })
                );
                openEditor({
                    ...file,
                    ext,
                    id: data.home,
                    name: data.home,
                    home: data.home,
                    size: data.size,
                });
            }
        } catch (error) {
            snackbarController.showSnackbar({
                id: 'create-copy-of-non-editable-file-error',
                text: 'Не удалось создать документ',
                type: SnackbarTypes.failure,
                ...snackBarCommonProps,
            });

            techAnalytics(TechAnalyticsType.apiError);
        }

        onClose?.();
    };

    return useCallback(handleCreateCopyOfFile, [
        convertedStorage,
        dispatch,
        ext,
        file,
        fromSharePopup,
        isSharedFile,
        onClose,
        techAnalytics,
    ]);
};
